// src/components/Facility/FacilityListPage.jsx

import React, { useState, useEffect } from "react";
import {
  fetchFacilities,
  deleteFacility,
  bulkDeleteFacilities,
  changeFacilityStatus,
} from "../../services/facilityService";
import { Icon } from "@iconify/react";
import { Link, useNavigate } from "react-router-dom";
import SuccessAlert from "../../components/child/Alert/SuccessAlert";
import DengerAlert from "../../components/child/Alert/DengerAlert";
import { showConfirmationDialog } from "../../utils/apiHelper";

const FacilitiesListPage = () => {
  const [items, setItems] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [sortColumn, setSortColumn] = useState("createdAt");
  const [sortOrder, setSortOrder] = useState("desc");
  const [searchQuery, setSearchQuery] = useState("");
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);

  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [statusFilter, setStatusFilter] = useState("");

  const navigate = useNavigate();

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await fetchFacilities({
        page: currentPage,
        limit: itemsPerPage,
        search: searchQuery,
        sortBy: sortColumn,
        sortOrder: sortOrder,
        status: statusFilter,
      });
      setItems(res.data.data);
      setTotalItems(res.data.pagination.total);
      setTotalPages(res.data.pagination.totalPages);
    } catch (error) {
      setErrorMessage("Failed to fetch facilities");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, [
    searchQuery,
    currentPage,
    itemsPerPage,
    sortColumn,
    sortOrder,
    statusFilter,
  ]);

  const handleSort = (column) => {
    if (sortColumn === column) {
      setSortOrder(sortOrder === "asc" ? "desc" : "asc");
    } else {
      setSortColumn(column);
      setSortOrder("asc");
    }
  };

  const handleSelectAll = (e) => {
    if (e.target.checked) {
      setSelectedItems(items.map((item) => item._id));
    } else {
      setSelectedItems([]);
    }
  };

  const handleSelectItem = (id) => {
    setSelectedItems((prev) =>
      prev.includes(id) ? prev.filter((i) => i !== id) : [...prev, id]
    );
  };

  const isSelected = (id) => selectedItems.includes(id);

  const handleDeleteClick = async (id = null) => {
    const message = id
      ? "This action will delete the facility!"
      : "This action will delete selected facilities!";
    const confirmMessage = "Yes, delete it!";

    const result = await showConfirmationDialog(message, confirmMessage);
    if (!result.isConfirmed) return;

    try {
      let response;

      if (id) {
        response = await deleteFacility(id);
      } else {
        if (selectedItems.length === 0) {
          setErrorMessage("Please select facilities to delete.");
          setTimeout(() => setErrorMessage(""), 3000);
          return;
        }
        response = await bulkDeleteFacilities(selectedItems);
      }

      const successText =
        response?.data?.message ||
        response?.message ||
        "Facility deleted successfully";

      setSuccessMessage(successText);
      setSelectedItems([]);
      fetchData();

      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      const errorText =
        err?.response?.data?.message ||
        err?.message ||
        "Error deleting facility";
      setErrorMessage(errorText);
      setTimeout(() => setErrorMessage(""), 3000);
    }
  };

  const handleStatusClick = async (id, currentStatus) => {
    const newStatus = currentStatus === "active" ? "inactive" : "active";
    const message = `This action will change the status to ${newStatus}.`;
    const confirmMessage = "Yes, change it!";

    const result = await showConfirmationDialog(message, confirmMessage);
    if (!result.isConfirmed) return;

    try {
      const response = await changeFacilityStatus(id, { status: newStatus });

      const successText =
        response?.data?.message ||
        response?.message ||
        "Status updated successfully";

      setSuccessMessage(successText);
      fetchData();

      setTimeout(() => setSuccessMessage(""), 3000);
    } catch (err) {
      const errorText =
        err?.response?.data?.message || err?.message || "Error updating status";
      setErrorMessage(errorText);
      setTimeout(() => setErrorMessage(""), 3000);
    }
  };

  const getPaginationRange = () => {
    const totalPages = Math.ceil(totalItems / itemsPerPage);
    const pages = [];
    for (let i = 1; i <= totalPages; i++) pages.push(i);
    return pages;
  };

  return (
    <div className="card h-100 p-0 radius-12">
      {successMessage && (
        <SuccessAlert
          message={successMessage}
          onClose={() => setSuccessMessage("")}
        />
      )}
      {errorMessage && (
        <DengerAlert
          message={errorMessage}
          onClose={() => setErrorMessage("")}
        />
      )}

      {/* Header and Filters */}
      <div className="card-header border-bottom bg-base py-16 px-24 d-flex align-items-center flex-wrap gap-3 justify-content-between">
        <div className="d-flex align-items-center flex-wrap gap-3">
          <span className="text-md fw-medium text-secondary-light mb-0">
            Show
          </span>

          <select
            className="form-select form-select-sm w-auto ps-12 py-6"
            style={{
              fontSize: "16px",
              borderRadius: "7px",
              backgroundPositionX: "unset",
              backgroundPosition: "right 0.75rem center",
            }}
            value={itemsPerPage}
            onChange={(e) => {
              setItemsPerPage(parseInt(e.target.value));
              setCurrentPage(1);
            }}
          >
            <option value="10">10 items per page</option>
            <option value="20">20 items per page</option>
            <option value="50">50 items per page</option>
            <option value="100">100 items per page</option>
          </select>

          <form className="navbar-search" style={{ position: "relative" }}>
            <input
              type="text"
              className="bg-base h-40-px w-auto pe-40"
              name="search"
              placeholder="Search facilities"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyUp={(e) => {
                if (e.target.value === "") {
                  setCurrentPage(1);
                }
              }}
              style={{
                paddingRight: searchQuery ? "5rem" : "2.5rem",
              }}
            />
            <Icon
              icon="ion:search-outline"
              className="icon"
              style={{
                position: "absolute",
                right: searchQuery ? "3.25rem" : "1rem",
                top: "50%",
                transform: "translateY(-50%)",
                fontSize: "1.25rem",
                color: "#999",
                pointerEvents: "none",
              }}
            />
            {searchQuery && (
              <button
                type="button"
                onClick={() => {
                  setSearchQuery("");
                  setCurrentPage(1);
                }}
                style={{
                  position: "absolute",
                  right: "1rem",
                  top: "30%",
                  transform: "translateY(-50%)",
                  fontSize: "1.25rem",
                  color: "#000",
                  background: "transparent",
                  border: "none",
                  cursor: "pointer",
                }}
              >
                <Icon icon="ic:round-close" />
              </button>
            )}
          </form>

          <select
            className="form-select form-select-sm w-auto ps-12 py-6"
            style={{
              fontSize: "16px",
              borderRadius: "7px",
              backgroundPositionX: "unset",
              backgroundPosition: "right 0.75rem center",
            }}
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setCurrentPage(1);
            }}
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>

          <button
            className="btn btn-danger text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
            disabled={selectedItems.length === 0}
            onClick={() => handleDeleteClick(null)}
          >
            Delete All
          </button>
        </div>

        {/* Add Facility */}
        <div className="ms-auto">
          <Link
            to="/facility/add"
            className="btn btn-primary text-sm btn-sm px-12 py-12 radius-8 d-flex align-items-center gap-2"
          >
            <Icon
              icon="ic:baseline-plus"
              className="icon text-xl line-height-1"
            />
            Add Facility
          </Link>
        </div>
      </div>

      {/* Table */}
      <div className="card-body p-24">
        <div className="table-responsive scroll-sm">
          <table className="table bordered-table sm-table mb-0">
            <thead>
              <tr>
                <th>
                  <input
                    type="checkbox"
                    className="form-check-input"
                    onChange={handleSelectAll}
                    checked={
                      selectedItems.length === items.length && items.length > 0
                    }
                  />
                </th>
                <th
                  onClick={() => handleSort("name")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="d-flex align-items-center gap-2">
                    Name
                    <span className="sort-colume">
                      <Icon
                        icon="mdi:arrow-up"
                        className={`arrow ${
                          sortColumn === "name" && sortOrder === "asc"
                            ? "active"
                            : ""
                        }`}
                      />
                      <Icon
                        icon="mdi:arrow-down"
                        className={`arrow ${
                          sortColumn === "name" && sortOrder === "desc"
                            ? "active"
                            : ""
                        }`}
                      />
                    </span>
                  </div>
                </th>
                <th
                  onClick={() => handleSort("createdAt")}
                  style={{ cursor: "pointer" }}
                >
                  <div className="d-flex align-items-center gap-2">
                    Date
                    <span className="sort-colume">
                      <Icon
                        icon="mdi:arrow-up"
                        className={`arrow ${
                          sortColumn === "createdAt" && sortOrder === "asc"
                            ? "active"
                            : ""
                        }`}
                      />
                      <Icon
                        icon="mdi:arrow-down"
                        className={`arrow ${
                          sortColumn === "createdAt" && sortOrder === "desc"
                            ? "active"
                            : ""
                        }`}
                      />
                    </span>
                  </div>
                </th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan="5" className="text-center">
                    <span className="loader-15"></span>
                  </td>
                </tr>
              ) : items.length > 0 ? (
                items.map((fac) => (
                  <tr key={fac._id}>
                    <td>
                      <div className="d-flex align-items-center gap-10">
                        <div className="form-check style-check d-flex align-items-center">
                          <input
                            className="form-check-input radius-4 border border-neutral-400"
                            type="checkbox"
                            name="checkbox"
                            value={fac._id}
                            checked={isSelected(fac._id)}
                            onChange={() => handleSelectItem(fac._id)}
                          />
                        </div>
                      </div>
                    </td>
                    <td>{fac.name}</td>
                    <td>
                      {(() => {
                        const date = new Date(fac.createdAt);
                        const day = String(date.getDate()).padStart(2, "0");
                        const month = date.getMonth() + 1;
                        const year = date.getFullYear();
                        return `${day}-${month}-${year}`;
                      })()}
                    </td>
                    <td>
                      <span
                        onClick={() => handleStatusClick(fac._id, fac.status)}
                        className={`${
                          fac.status === "active"
                            ? "bg-success-focus text-success-600 border border-success-main px-24 py-4 radius-4 fw-medium text-sm"
                            : "bg-neutral-200 text-neutral-600 border border-neutral-400 px-24 py-4 radius-4 fw-medium text-sm"
                        }`}
                        style={{ cursor: "pointer" }}
                        title={
                          fac.status === "active"
                            ? "Click to Deactivate"
                            : "Click to Activate"
                        }
                      >
                        {fac.status.charAt(0).toUpperCase() +
                          fac.status.slice(1)}
                      </span>
                    </td>
                    <td>
                      <div className="d-flex align-items-center gap-10">
                        <button
                          type="button"
                          title="Edit"
                          className="bg-success-focus text-success-600 bg-hover-success-200 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                          onClick={() => {
                            navigate(`/facility/edit/${fac._id}`);
                          }}
                        >
                          <Icon icon="lucide:edit" className="menu-icon" />
                        </button>

                        <button
                          type="button"
                          title="Delete"
                          className="remove-item-btn bg-danger-focus bg-hover-danger-200 text-danger-600 fw-medium w-40-px h-40-px d-flex justify-content-center align-items-center rounded-circle"
                          onClick={() => handleDeleteClick(fac._id, true)}
                        >
                          <Icon
                            icon="fluent:delete-24-regular"
                            className="menu-icon"
                          />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan="5" className="text-center">
                    No Data Found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mt-24">
          <span>
            Showing {(currentPage - 1) * itemsPerPage + 1} to{" "}
            {Math.min(currentPage * itemsPerPage, totalItems)} of {totalItems}{" "}
            entries
          </span>

          <ul className="pagination d-flex flex-wrap align-items-center gap-2 justify-content-center">
            <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
              <button
                className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                onClick={() => {
                  if (currentPage > 1) setCurrentPage((prev) => prev - 1);
                }}
              >
                <Icon icon="ep:d-arrow-left" />
              </button>
            </li>

            {getPaginationRange().map((pageNum) => (
              <li key={pageNum} className="page-item">
                <button
                  className={`page-link text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px w-32-px text-md ${
                    currentPage === pageNum
                      ? "bg-primary-600 text-white"
                      : "bg-neutral-200"
                  }`}
                  onClick={() => setCurrentPage(pageNum)}
                >
                  {pageNum}
                </button>
              </li>
            ))}

            <li className="page-item">
              <button
                className="page-link bg-neutral-200 text-secondary-light fw-semibold radius-8 border-0 d-flex align-items-center justify-content-center h-32-px text-md"
                disabled={currentPage === totalPages}
                onClick={() => {
                  if (currentPage < totalPages) setCurrentPage(currentPage + 1);
                }}
              >
                <Icon icon="ep:d-arrow-right" />
              </button>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default FacilitiesListPage;
